import { stripStoreLinks } from '../../lib/storeLinks.js';
import { AttachPicker, AttachTray, TurnAttachments } from './Attachments.js';
import { rememberPreview, useAttachments } from '../../lib/assistantAttachments.js';
import {
  plainReply,
  type AssistantActivity,
  type AssistantAttachment,
  type AssistantTurn,
} from '@secretario/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'wouter';
import { getAssistantHistory, streamAssistantMessage } from '../../lib/api/assistant.js';
import { errorCode } from '../../lib/api/http.js';
import { ASSISTANT_ROUTE, type AssistantPlaceRef } from '../../lib/assistantBus.js';
import { ChoiceButtons, choicesOf } from './ChoiceButtons.js';
import { TurnText } from './TurnText.js';
import { pendingDraft } from '../../lib/assistantDraft.js';
import { playWatch, setWatchPref, useWatchPref } from '../../lib/watchMode.js';
import { pendingConfirm } from '../../lib/assistantConfirm.js';
import { ServiceRequestCard } from './ServiceRequestCard.js';
import { PendingActionCard } from './PendingActionCard.js';
import { pendingAction } from '../../lib/assistantPending.js';
import { characterImage, DEFAULT_CHARACTER } from '../../lib/characters.js';
import {
  canDictate,
  hapticSuccess,
  hapticTap,
  startDictation,
  type Dictation,
} from '../../lib/native.js';
import { useSession } from '../../lib/session.js';
import { openPaywall } from '../../lib/plan.js';
import { trackOnce } from '../../lib/track.js';
import { useProfile } from '../../lib/useProfile.js';
import { IconChat, IconClose, IconMic, IconSend } from '../chrome/icons.js';
import { DraftCard } from './DraftCard.js';
import { ResultCards } from './ResultCards.js';
import { panelOfCard } from '../stage/cardView.js';
import { useOptionalStage } from '../stage/StageProvider.js';
import { stepOf } from './steps.js';
import './assistant-modal.css';

const HISTORY_KEY = ['assistant', 'history'] as const;

type Phase = 'idle' | 'listening' | 'thinking' | 'result' | 'sent';

const SUGGESTIONS = [
  'O que eu tenho hoje?',
  'Manda uma mensagem pro João dizendo que chego às 18h',
  'Vai chover amanhã?',
];

/** Como cada passo aparece enquanto a equipe trabalha: quem foi chamado e para quê. */
/**
 * Conversa especial com o Secretário, aberta pelo microfone: ele ouve (ou lê),
 * mostra quem da equipe está trabalhando e devolve a resposta num cartão
 * próprio. Mensagens para outras pessoas viram um rascunho que só sai com o
 * toque em Enviar. A conversa completa continua em "Abrir conversa".
 */
export function AssistantModal({
  open,
  autoListen,
  prefillText,
  lugar = null,
  onClose,
}: {
  open: boolean;
  autoListen: boolean;
  prefillText: string;
  /** Aberto da página de um lugar do Perto: o pedido é sobre aquela unidade. */
  lugar?: AssistantPlaceRef | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const watching = useWatchPref();
  const [, navigate] = useLocation();
  const stage = useOptionalStage();
  const session = useSession();
  const profileQ = useProfile();
  const historyQ = useQuery({
    queryKey: HISTORY_KEY,
    queryFn: getAssistantHistory,
    enabled: open,
    staleTime: 5_000,
  });
  const characterKey = profileQ.data?.characterKey ?? DEFAULT_CHARACTER;
  const name = profileQ.data?.secretaryName?.trim() || 'Seu secretário';
  const first =
    (profileQ.data?.nickname ?? session?.user.displayName ?? '').trim().split(/\s+/)[0] ?? '';

  const [phase, setPhaseState] = useState<Phase>('idle');
  // A fase também fica numa referência: o fim da escuta precisa saber onde estamos.
  const phaseRef = useRef<Phase>('idle');
  const setPhase = (next: Phase) => {
    phaseRef.current = next;
    setPhaseState(next);
  };
  const [text, setText] = useState('');
  const [heard, setHeard] = useState('');
  // Últimos segundos de silêncio antes de enviar sozinho (null enquanto há fala).
  const [sendIn, setSendIn] = useState<number | null>(null);
  const [asked, setAsked] = useState('');
  const attach = useAttachments();
  const [askedFiles, setAskedFiles] = useState<AssistantAttachment[]>([]);
  // O seletor de anexo está aberto: o fim do ditado (o Android para o microfone quando
  // outra tela abre por cima) não envia; quando a pessoa volta, o microfone volta a ouvir.
  const pickingRef = useRef<{ resume: boolean } | null>(null);
  const [activities, setActivities] = useState<AssistantActivity[]>([]);
  const [reply, setReply] = useState('');
  const [error, setError] = useState<{ message: string; limit: boolean } | null>(null);
  const [sentTo, setSentTo] = useState<{ name: string; roomId: string } | null>(null);
  // O lugar de que se fala (veio da página dele ou de uma opção tocada); vai junto em cada pedido.
  const [about, setAbout] = useState<AssistantPlaceRef | null>(null);
  const [, refresh] = useState(0);
  const dictationRef = useRef<Dictation | null>(null);
  const heardRef = useRef('');
  const abortRef = useRef<AbortController | null>(null);
  const dictation = canDictate();

  function reset() {
    setPhase('idle');
    setText('');
    setHeard('');
    setAsked('');
    setAskedFiles([]);
    attach.clear();
    pickingRef.current = null;
    setActivities([]);
    setReply('');
    setError(null);
    setSentTo(null);
    setAbout(null);
    heardRef.current = '';
  }

  function close() {
    dictationRef.current?.stop();
    dictationRef.current = null;
    abortRef.current?.abort();
    reset();
    onClose();
  }

  async function send(message: string, lugarId?: string) {
    const body = message.trim();
    if (!body && attach.count === 0) {
      setPhase('idle');
      return;
    }
    hapticTap();
    setAsked(body);
    const chosen = attach.items;
    for (const item of chosen) rememberPreview(item.id ?? item.key, item.preview);
    setAskedFiles(
      chosen
        .filter((item) => item.state !== 'failed')
        .map((item) => ({
          id: item.id ?? item.key,
          kind: item.kind,
          name: item.name,
          mime: '',
          size: 0,
        })),
    );
    setText('');
    setError(null);
    setActivities([]);
    setReply('');
    setPhase('thinking');
    // Espera só o que ainda sobe; a fala já está na tela.
    const { ids } = chosen.length
      ? await attach.settle(chosen.map((item) => item.key))
      : { ids: [] as string[] };
    attach.clear();
    if (!body && ids.length === 0) {
      setPhase('idle');
      setError({ message: 'Não consegui mandar o anexo. Tenta de novo?', limit: false });
      return;
    }
    const abort = new AbortController();
    abortRef.current = abort;
    let failed = false;
    try {
      await streamAssistantMessage(
        body,
        (event) => {
          if (event.type === 'activity') {
            setActivities((current) => [
              ...current.filter((item) => item.id !== event.activity.id),
              event.activity,
            ]);
          } else if (event.type === 'delta') {
            setReply((current) => current + event.text);
            setPhase('result');
          } else if (event.type === 'done') {
            trackOnce('activated', { surface: 'microfone' });
            setReply(event.turn.text);
            setActivities(event.turn.activities);
            // "Abre a agenda" (F3) ou a ação feita com o Assistir ligado: a tela certa abre por trás.
            followTurn(event.turn.activities);
            qc.setQueryData<AssistantTurn[]>(HISTORY_KEY, (current) => {
              const known = new Set((current ?? []).map((turn) => turn.id));
              return known.has(event.turn.id) ? (current ?? []) : [...(current ?? []), event.turn];
            });
          } else if (event.type === 'error') {
            failed = true;
            setError({ message: event.message, limit: false });
          }
        },
        abort.signal,
        ids,
        undefined,
        lugarId ?? about?.id,
      );
    } catch (err) {
      failed = true;
      const limit = errorCode(err) === 'ai_limit_reached';
      if (limit) openPaywall('limit', 'microfone');
      setError({
        message:
          (err as { message?: string } | null)?.message ||
          'Não consegui te ouvir agora. Tenta de novo em instantes?',
        limit,
      });
    } finally {
      abortRef.current = null;
      // O que o Secretário fez (lembrete, agenda, memória) aparece nas outras telas.
      void qc.invalidateQueries();
    }
    if (failed) {
      setText(body);
      setPhase('idle');
    } else {
      hapticSuccess();
      setPhase('result');
    }
  }

  /** Ouve; `before` é o que já foi dito antes de uma pausa (o seletor de anexo). */
  function listen(before = '') {
    if (!dictation || (phaseRef.current === 'listening' && !before)) return;
    setError(null);
    setHeard(before);
    setSendIn(null);
    heardRef.current = before;
    setPhase('listening');
    void startDictation({
      onText: (value) => {
        const joined = before ? `${before} ${value}`.trim() : value;
        heardRef.current = joined;
        setHeard(joined);
      },
      onSilence: setSendIn,
      onEnd: () => {
        dictationRef.current = null;
        // Foi escolher um anexo: guarda o que ouviu e espera a volta.
        if (pickingRef.current) return;
        // Parou de falar: o pedido segue sozinho; sem fala, volta ao começo.
        if (phaseRef.current !== 'listening') return;
        const said = heardRef.current.trim();
        if (said || attach.count > 0) {
          void send(said);
        } else {
          setPhase('idle');
          setError({
            message: 'Não te ouvi dessa vez. Toca no microfone e fala de novo.',
            limit: false,
          });
        }
      },
    }).then((handle) => {
      dictationRef.current = handle;
      if (!handle) {
        setPhase('idle');
        setError({
          message: 'Não consegui usar o microfone. Pode escrever aqui embaixo.',
          limit: false,
        });
      }
    });
  }

  // Abriu pelo microfone central: já começa ouvindo. Abriu por uma sugestão: traz o texto.
  const started = useRef(false);
  useEffect(() => {
    if (!open) {
      started.current = false;
      return;
    }
    if (started.current) return;
    started.current = true;
    reset();
    setAbout(lugar);
    if (prefillText) setText(prefillText);
    else if (autoListen && dictation) listen();
  }, [open]);

  useEffect(
    () => () => {
      dictationRef.current?.stop();
      abortRef.current?.abort();
    },
    [],
  );

  if (!open) return null;

  const expression =
    phase === 'listening'
      ? 'tranquilo'
      : phase === 'thinking'
        ? 'direto'
        : phase === 'sent'
          ? 'divertido'
          : 'simpatico';
  const draft = phase === 'result' ? pendingDraft(activities) : null;
  // A resposta terminou numa pergunta: o campo vira "Responder" (é a continuação da conversa).
  const asking = phase === 'result' && reply.trim().endsWith('?');
  const confirm = phase === 'result' ? pendingConfirm(activities) : null;
  const action = phase === 'result' ? pendingAction(activities) : null;
  const noted = activities.some(
    (item) => item.tool === 'guardar_na_memoria' && item.state === 'done',
  );
  const recent = (historyQ.data ?? []).slice(-2);

  /** A tela que o resultado pede (abrir) ou a repetição do Assistir; o balão sai da frente. */
  function followTurn(list: AssistantActivity[]) {
    const script = list.find((row) => row.watch)?.watch ?? null;
    const target = list.find((row) => row.open)?.open ?? null;
    if (script && watching && playWatch(script)) {
      window.setTimeout(onClose, 450);
      return;
    }
    if (target) {
      navigate(target);
      window.setTimeout(onClose, 450);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    void send(text);
  }

  function pickStart() {
    const listening = phaseRef.current === 'listening';
    pickingRef.current = { resume: listening };
    if (listening) {
      setSendIn(null);
      dictationRef.current?.stop();
    }
  }

  function pickEnd() {
    const picking = pickingRef.current;
    pickingRef.current = null;
    // Estava ouvindo: volta a ouvir, com o que já tinha sido dito.
    if (picking?.resume && phaseRef.current === 'listening') {
      setTimeout(() => listen(heardRef.current.trim() || ' '), 250);
    }
  }

  function openConversation() {
    close();
    navigate(ASSISTANT_ROUTE);
  }

  return createPortal(
    <div
      className="am-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={`Falar com ${name}`}
      onClick={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div className={`am-card am-${phase}`}>
        <div className="am-atmosphere" aria-hidden="true">
          <i />
          <i />
          <i />
        </div>
        <header className="am-top">
          <span className="am-top-left">
            <button className="icon-btn" onClick={openConversation} aria-label="Abrir conversa">
              <IconChat />
            </button>
            {(phase === 'idle' || phase === 'listening') && (
              <AttachPicker
                className="icon-btn"
                up={false}
                onFiles={attach.add}
                onPickStart={pickStart}
                onPickEnd={pickEnd}
              />
            )}
          </span>
          <span className="am-grip" aria-hidden="true" />
          {/* O modo Assistir: com ele ligado, a tela mostra ele fazendo o que você pediu. */}
          <span className="am-top-right">
            <button
              type="button"
              role="switch"
              aria-checked={watching}
              aria-label={`Assistir: ${watching ? 'ligado' : 'desligado'}`}
              className={`am-watch${watching ? ' on' : ''}`}
              onClick={() => setWatchPref(!watching)}
            >
              <i aria-hidden="true" />
              Assistir
            </button>
            <button className="icon-btn" onClick={close} aria-label="Fechar">
              <IconClose />
            </button>
          </span>
        </header>

        <div className="am-stage" aria-hidden="true">
          <span className="am-halo">
            <i />
            <i />
            <i />
          </span>
          <img key={expression} src={characterImage(characterKey, expression)} alt="" />
        </div>

        <div className="am-body">
          {phase === 'idle' && (
            <>
              <h2>{first ? `Oi, ${first}!` : 'Oi!'} O que eu faço por você?</h2>
              <p className="am-sub">Fale ou escreva. Eu mostro antes de fazer qualquer coisa.</p>
              {error && (
                <p className="am-note warn">
                  {error.message}{' '}
                  {error.limit && (
                    <button type="button" onClick={() => openPaywall('limit', 'microfone')}>
                      Ver os planos
                    </button>
                  )}
                </p>
              )}
              {dictation && (
                <button className="am-mic" onClick={() => listen()} aria-label="Falar">
                  <IconMic />
                </button>
              )}
              <AttachTray items={attach.items} notice={attach.notice} onRemove={attach.remove} />
              {about && <AboutChip about={about} onClear={() => setAbout(null)} />}
              <form className="am-field" onSubmit={submit}>
                <input
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  placeholder={dictation ? 'Ou escreva aqui' : `Escreva para ${name}`}
                  maxLength={2000}
                  aria-label="Seu pedido"
                  enterKeyHint="send"
                />
                <button
                  type="submit"
                  disabled={!text.trim() && attach.count === 0}
                  aria-label="Enviar"
                >
                  <IconSend />
                </button>
              </form>
              {recent.length > 0 ? (
                <div className="am-recent" aria-label="Nossa conversa de hoje">
                  <span className="am-kicker">Na nossa conversa</span>
                  {recent.map((turn) => (
                    <p key={turn.id} className={turn.role}>
                      {stripStoreLinks(turn.text).replace(/\n[ \t]*-[ \t]+/g, ' · ')}
                    </p>
                  ))}
                </div>
              ) : (
                <div className="am-chips">
                  {SUGGESTIONS.map((suggestion) => (
                    <button key={suggestion} onClick={() => void send(suggestion)}>
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {phase === 'listening' && (
            <>
              <h2>Estou te ouvindo</h2>
              <p className={`am-heard${heard.trim() ? '' : ' waiting'}`} aria-live="polite">
                {heard.trim() || (attach.count ? 'Pode falar, ou toque em Pronto…' : 'Pode falar…')}
              </p>
              <AttachTray items={attach.items} notice={attach.notice} onRemove={attach.remove} />
              <p className={`am-sendin${sendIn !== null && heard ? ' on' : ''}`} aria-hidden="true">
                {sendIn !== null && heard
                  ? sendIn > 0
                    ? `Envio em ${sendIn} s, ou toque em Pronto`
                    : 'Enviando'
                  : ' '}
              </p>
              <button
                className="am-primary wide"
                onClick={() => {
                  if (dictationRef.current) dictationRef.current.stop();
                  else void send(heardRef.current);
                }}
                disabled={!heard.trim() && attach.count === 0}
              >
                Pronto
              </button>
              <button
                className="am-ghost"
                onClick={() => {
                  heardRef.current = '';
                  setHeard('');
                  dictationRef.current?.stop();
                  setPhase('idle');
                }}
              >
                Cancelar
              </button>
            </>
          )}

          {(phase === 'thinking' || phase === 'result') && (asked || askedFiles.length > 0) && (
            <>
              {askedFiles.length > 0 && (
                <div className="am-asked-files">
                  <TurnAttachments attachments={askedFiles} />
                </div>
              )}
              {asked && <p className="am-asked">“{asked}”</p>}
            </>
          )}

          {phase === 'thinking' && (
            <>
              <h2>Já estou cuidando disso</h2>
              <ol className="am-journey" aria-live="polite">
                <li className="done">
                  <span className="am-art">
                    <img src={characterImage(characterKey, 'direto')} alt="" />
                  </span>
                  <span>
                    <b>Entendendo o pedido</b>
                    <small>Separando o que precisa trabalhar junto.</small>
                  </span>
                  <i aria-hidden="true" />
                </li>
                {activities.map((activity) => {
                  const step = stepOf(activity);
                  return (
                    <li key={activity.id} className={activity.state}>
                      <span className="am-art">
                        <img src={step.art} alt="" />
                      </span>
                      <span>
                        <b>{step.label}</b>
                        <small>{step.detail}</small>
                      </span>
                      <i aria-hidden="true" />
                    </li>
                  );
                })}
                {activities.every((activity) => activity.state !== 'running') && (
                  <li className="running">
                    <span className="am-art">
                      <img src={characterImage(characterKey, 'simpatico')} alt="" />
                    </span>
                    <span>
                      <b>Preparando sua resposta</b>
                      <small>Juntando tudo de um jeito claro.</small>
                    </span>
                    <i aria-hidden="true" />
                  </li>
                )}
              </ol>
            </>
          )}

          {phase === 'result' && (
            <>
              {activities.length > 0 && (
                <div className="am-team" aria-label="Quem trabalhou neste pedido">
                  <span className="am-kicker">O que eu fiz</span>
                  <div>
                    {activities.map((activity) => (
                      <span key={activity.id} className={`am-team-chip ${activity.state}`}>
                        <img src={stepOf(activity).art} alt="" />
                        {stepOf(activity).label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <article className="am-answer">
                <span className="am-kicker">{name} diz</span>
                <TurnText text={plainReply(reply)} />
              </article>
              {/* Opções para responder com um toque (as unidades de uma rede, por exemplo). */}
              <ChoiceButtons
                choices={choicesOf(activities)}
                onPick={(choice) => {
                  if (choice.lugar)
                    setAbout({ id: choice.lugar, name: choice.label, where: choice.sub });
                  void send(choice.send, choice.lugar ?? undefined);
                }}
              />
              <ResultCards
                items={activities}
                onOpen={close}
                {...(stage
                  ? {
                      onPick: (card) => {
                        const kind = panelOfCard(card);
                        if (!kind) return false;
                        close();
                        stage.show({ kind, source: 'assistant' });
                        return true;
                      },
                    }
                  : {})}
              />
              {noted && (
                <button
                  className="am-noted"
                  onClick={() => {
                    close();
                    navigate('/sobre-voce');
                  }}
                >
                  <b>Anotei</b> ver o que eu sei de você <span aria-hidden="true">›</span>
                </button>
              )}
              {draft && (
                <DraftCard
                  key={draft.activityId}
                  activityId={draft.activityId}
                  draft={draft.action}
                  onSent={(roomId) => {
                    setSentTo({ name: draft.action.contactName, roomId });
                    setPhase('sent');
                  }}
                  onDismiss={() => refresh((count) => count + 1)}
                />
              )}
              {confirm && (
                <ServiceRequestCard
                  key={confirm.activityId}
                  activityId={confirm.activityId}
                  confirm={confirm.confirm}
                  onDismiss={() => refresh((count) => count + 1)}
                />
              )}
              {action && (
                <PendingActionCard
                  key={action.id}
                  pending={action}
                  onDecided={(turn) => {
                    // A resposta depois do toque substitui a pergunta no balão.
                    if (turn) {
                      setReply(turn.text);
                      setActivities(turn.activities);
                      followTurn(turn.activities);
                    } else refresh((count) => count + 1);
                    void qc.invalidateQueries({ queryKey: HISTORY_KEY });
                  }}
                />
              )}
              {about && <AboutChip about={about} onClear={() => setAbout(null)} />}
              <form className="am-field follow" onSubmit={submit}>
                {dictation && (
                  <button
                    type="button"
                    className="am-field-mic"
                    onClick={() => listen()}
                    aria-label="Falar"
                  >
                    <IconMic />
                  </button>
                )}
                <input
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  placeholder={asking ? `Responder a ${name}` : 'Pedir outra coisa'}
                  maxLength={2000}
                  aria-label={asking ? `Responder a ${name}` : 'Outro pedido'}
                  enterKeyHint="send"
                />
                <button type="submit" disabled={!text.trim()} aria-label="Enviar">
                  <IconSend />
                </button>
              </form>
              <button className="am-link" onClick={openConversation}>
                Abrir conversa completa
              </button>
            </>
          )}

          {phase === 'sent' && sentTo && (
            <>
              <h2>Prontinho</h2>
              <p className="am-sub">Mensagem enviada para {sentTo.name}.</p>
              <button
                className="am-primary wide"
                onClick={() => {
                  const roomId = sentTo.roomId;
                  close();
                  navigate(`/conversas/${encodeURIComponent(roomId)}`);
                }}
              >
                Ver a conversa
              </button>
              <button className="am-ghost" onClick={reset}>
                Pedir outra coisa
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** De que lugar se fala: aparece acima do campo e vai junto em cada pedido; o X tira. */
function AboutChip({ about, onClear }: { about: AssistantPlaceRef; onClear: () => void }) {
  return (
    <div className="ch-about">
      <span>
        Sobre <b>{about.name}</b>
        {about.where ? ` · ${about.where}` : ''}
      </span>
      <button type="button" aria-label="Não é sobre este lugar" onClick={onClear}>
        ×
      </button>
    </div>
  );
}
